import { Button } from "@ui/Button";
import { toast } from "@ui/molecules/Toasts/toast";
import { dispatchVideoPreviewPauseAll } from "@ui/molecules/VideoPreview/videoPreviewVolume";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { type Accessor, createSignal } from "solid-js";
import { type BoardsByVideoIdsEntryResponse, getBoardClip } from "@/client";
import type { VideoEvidence } from "../ChatEventTypes";
import type { EditClipEventDetail } from "./ChatLayout/ChatLayout";

type Props = {
	existingBoard: Accessor<BoardsByVideoIdsEntryResponse | undefined>;
	membershipLoading?: Accessor<boolean>;
	videoEvidence: VideoEvidence;
	addClip: (clip: VideoEvidence) => Promise<void>;
};

const isClipOnBoard = (entry?: BoardsByVideoIdsEntryResponse) =>
	!!(entry?.board && entry?.clip);

export const AddToBoardButton = (props: Props) => {
	const [pendingAdd, setPendingAdd] = createSignal(false);
	const membershipLoading = () => props.membershipLoading?.() ?? false;
	const inBoard = () =>
		!membershipLoading() &&
		(isClipOnBoard(props.existingBoard()) || pendingAdd());
	const tip = () => {
		if (membershipLoading()) {
			return "Loading…";
		}
		return inBoard() ? <>Update</> : "Add to board";
	};

	return (
		<TooltipTrigger keepOpenOnClick>
			<Button
				shape="circle"
				theme="black"
				classList={{ "in-board": inBoard() }}
				symbol={
					membershipLoading()
						? "progress_activity"
						: inBoard()
							? "bookmark_added"
							: "bookmark_add"
				}
				symbolClass={membershipLoading() ? "animate-spin" : undefined}
				symbolFilled={inBoard()}
				symbolRelativeSize="even-larger"
				disabled={membershipLoading() || pendingAdd()}
				onClick={async () => {
					dispatchVideoPreviewPauseAll();

					if (membershipLoading()) {
						return;
					}

					if (inBoard()) {
						const existingBoard = props.existingBoard();
						let clipForPanel = existingBoard?.clip;
						const boardId = existingBoard?.board?.id;
						const clipId = existingBoard?.clip?.id;
						if (boardId && clipId) {
							const res = await getBoardClip({
								path: {
									board_id: boardId,
									clip_id: clipId,
								},
								query: { num_frames: 50 },
							});
							if (res.error) {
								toast.error({
									title: "Failed to load clip",
									description: (res.error as { message?: string }).message,
								});
								return;
							}
							clipForPanel = res.data;
						}
						const moveEvent: EditClipEventDetail = {
							type: "open_move_board_panel",
							clip: clipForPanel,
							video: {
								id: props.videoEvidence.video_id,
							},
							board: existingBoard?.board,
						};
						document.dispatchEvent(
							new CustomEvent("open_move_board_panel", {
								detail: moveEvent,
							}),
						);
						return;
					}

					setPendingAdd(true);
					try {
						await props.addClip(props.videoEvidence);
					} finally {
						setPendingAdd(false);
					}
				}}
			/>
			<Tooltip>{tip()}</Tooltip>
		</TooltipTrigger>
	);
};
